import { isValidFitKey } from '@vp/lat-react-component-library'
import type { Logger } from '@vp/ubik-logging'

import { getQuantitySelectionData } from '../../../../clients/BffClient'
import { getWork } from '../../../../clients/WorkEntityClient'
import type { Project } from '../../../../types/Project'
import type { QuantitySelectionData } from '../../../../types/QuantitySelectionData'
import type { Work } from '../../../../types/Work'

const loadWork = async (cartTenant: string, workId: string, authorizationHeader: string, logger: Logger): Promise<Work> => {
  const work = await getWork(cartTenant, workId, authorizationHeader, logger)
  if (work) {
    if (!work.product.key) {
      // Initialize product object since some works might not have it populated
      work.product.key = work.resources?.['productKey'] ?? work.design?.metadata?.productKey ?? null
    }

    return work
  }

  throw new Error('Unable to load work entity')
}

const loadBffData = async (
  tenant: string,
  culture: string,
  productKey: string,
  selectedOptions: Record<string, string>,
  productVersion: undefined | number,
  mpvId: string,
  logger: Logger,
): Promise<QuantitySelectionData> => {
  const bffData = await getQuantitySelectionData(tenant, culture, productKey, selectedOptions, logger, productVersion, mpvId)

  if (!bffData) {
    throw new Error(
      `Unable to load BFF Data for (culture:${culture}, productKey:${productKey}, selectedOptions:${JSON.stringify(
        selectedOptions,
      )}, productVersion:${productVersion}, mpvId:${mpvId}).`,
    )
  }
  return bffData
}

export const loadProject = async (
  tenant: string,
  cartTenant: string,
  culture: string,
  workId: string,
  authorizationHeader: string,
  logger: Logger,
): Promise<Project> => {
  const work = await loadWork(cartTenant, workId, authorizationHeader, logger)
  if (!work.product.key) {
    throw new Error(`Unable to load QSP without a product key for the customer work.`)
  }
  const bffData = await loadBffData(
    tenant,
    culture,
    work.product.key,
    work.merchandising.merchandisingSelections,
    work.product.version ? parseInt(work.product.version, 10) : undefined,
    work.resources['mpvId'] ?? '',
    logger,
  )

  const validatedSizeFitKey = isValidFitKey(bffData.sizeFitKey ?? '') ? bffData.sizeFitKey : null

  return {
    workId: work.workId,
    work: work,
    productName: bffData.productName,
    productKey: work.product.key,
    productVersion: bffData.productVersion,
    mpvId: bffData.mpvId,
    pageMode: bffData.pageMode,
    sizes: bffData.sizes,
    lowestPriceAttributes: bffData.lowestPriceAttributes,
    minimumOrderQuantity: bffData.minimumOrderQuantity,
    maximumOrderQuantity: bffData.maximumOrderQuantity,
    quantityIncrement: bffData.quantityIncrement,
    nextStepUrl: bffData.nextStepUrl,
    hasRelatedProducts: bffData.hasRelatedProducts,
    sizeFitKey: validatedSizeFitKey,
  }
}
